import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import { getLocation } from 'ducks/router'
import { useConfirmationModal, useModal } from 'hooks'
import { candidates, jobs } from 'orm'
import { useCandidateManagementModalForm } from './common/CandidateForm/hooks'

const useCandidateManagementModalQueries = () => {
  const dispatch = useDispatch()

  const handleFetchCandidates = React.useCallback(
    (
      { projectId, status },
      { limit, offset, ordering, search, is_favourite, hire_status },
    ) => {
      dispatch(
        candidates.fetchStart({
          project: projectId,
          limit,
          offset,
          ordering,
          search,
          status,
          is_favourite,
          hire_status,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleFetchCandidates,
  }
}

const useCandidateManagementModalMutations = () => {
  const dispatch = useDispatch()

  const handleCreateCandidate = React.useCallback(
    (
      { data },
      { onFailureAction, onSuccessAction, postSuccessAction } = {},
    ) => {
      dispatch(
        candidates.postStart({
          data,
          message: t`Candidate was successfully created.`,
          onFailureAction,
          onSuccessAction,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  const handleUpdateCandidate = React.useCallback(
    (
      { data, id },
      { onFailureAction, onSuccessAction, postSuccessAction } = {},
    ) => {
      dispatch(
        candidates.patchStart({
          pk: id,
          data,
          message: t`Candidate was successfully updated.`,
          onFailureAction,
          onSuccessAction,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleCreateCandidate,
    handleUpdateCandidate,
  }
}

export const useCandidateManagementModalState = (state, handlers) => {
  const { candidate, isOpen } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    payload: { id },
  } = useSelector(getLocation)
  const job = useSelector(state => jobs.getItem()(state, { pk: id }))

  const languages = job?.languages ?? []

  const {
    state: { control, formState, isDirtyOrInvalid },
    handlers: { handleFormReset, handleFormSubmit },
  } = useCandidateManagementModalForm(
    { candidate, languages, isOpen },
    { onConfirm },
  )

  const {
    handleConfirmationClose,
    handleConfirmationConfirm,
    handleConfirmationOpen,
    isConfirmationOpen,
  } = useConfirmationModal(() => {
    handleFormReset()
    onClose()
  })

  const handleCancel = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onCancel()
  }, [isDirtyOrInvalid, handleConfirmationOpen, onCancel])

  const handleClose = React.useCallback(() => {
    isDirtyOrInvalid ? handleConfirmationOpen() : onClose()
  }, [isDirtyOrInvalid, handleConfirmationOpen, onClose])

  const handleConfirm = React.useCallback(handleFormSubmit, [handleFormSubmit])

  return {
    state: {
      form: { control, formState },
      languages,
      isConfirmationOpen,
    },
    handlers: {
      handleCancel,
      handleClose,
      handleConfirm,
      handleConfirmationClose,
      handleConfirmationConfirm,
    },
  }
}

export const useCandidateManagementModal = (state, handlers = {}) => {
  const { candidate, projectId, status } = state
  const { handleReset } = handlers

  const { query = {} } = useSelector(getLocation)

  const { handleClose, handleOpen, isOpen } = useModal()

  const { handleFetchCandidates } = useCandidateManagementModalQueries()

  const { handleCreateCandidate, handleUpdateCandidate } =
    useCandidateManagementModalMutations()

  const handlePostSuccessAction = React.useCallback(() => {
    handleClose()
    handleReset()
    handleFetchCandidates({ projectId, status }, query)
  }, [
    projectId,
    query,
    status,
    handleClose,
    handleReset,
    handleFetchCandidates,
  ])

  const handleCandidateManagementModalClose = React.useCallback(() => {
    handleClose()
    handleReset()
  }, [handleClose, handleReset])

  const handleCandidateManagementModalConfirm = React.useCallback(
    (data, { onError, onSuccess } = {}) => {
      candidate
        ? handleUpdateCandidate(
            {
              data: { ...data, project: projectId },
              id: candidate.id,
            },
            {
              onFailureAction: onError,
              postSuccessAction: () => {
                handlePostSuccessAction()
                onSuccess()
              },
            },
          )
        : handleCreateCandidate(
            { data: { ...data, project: projectId } },
            {
              onFailureAction: onError,
              postSuccessAction: () => {
                handlePostSuccessAction()
                onSuccess()
              },
            },
          )
    },
    [
      candidate,
      projectId,
      handleCreateCandidate,
      handleUpdateCandidate,
      handlePostSuccessAction,
    ],
  )

  return {
    handleCandidateManagementModalClose,
    handleCandidateManagementModalConfirm,
    handleCandidateManagementModalOpen: handleOpen,
    isCandidateManagementModalOpen: isOpen,
  }
}
